import React, { useEffect } from 'react';
import { inDOM } from '../../utils';
import { LazyImageContext, LazyImageContextValues } from './LazyImageContext';

export type LazyProviderProps = {
    /**
     * The child components.
     */
    children: React.ReactNode;
};

function applyImageSrc(element: HTMLImageElement) {
    const { src, srcset } = element.dataset;
    const img = element;
    if (src && element.src !== src) {
        img.src = src;
        return;
    }
    if (srcset && element.srcset !== srcset) {
        img.srcset = srcset;
    }
}

function applyPictureSrc(element: HTMLPictureElement) {
    // Find the picture's source elements and set their srcset attribute
    const sources = Array.from(element.children).filter((child) => child.tagName === 'SOURCE');
    sources.forEach((source) => {
        const sourceElement = source as HTMLSourceElement;
        const { srcset: sourceSrcset } = sourceElement.dataset;
        if (sourceSrcset) {
            sourceElement.srcset = sourceSrcset;
        }
    });
}

export function LazyImageProvider(props: LazyProviderProps): React.JSX.Element {
    const { children } = props;

    const isClient = inDOM();
    const options = React.useMemo(
        () => ({
            rootMargin: '100px 100px',
        }),
        []
    );

    const observerRef = React.useRef<IntersectionObserver | null>(null);

    const callback = React.useCallback((entries: IntersectionObserverEntry[]) => {
        entries.forEach((entry) => {
            const target = entry.target as HTMLElement;
            const { src, srcset, inPicture, observed } = target.dataset;

            if (entry.isIntersecting) {
                // Stop observing this element
                observerRef.current?.unobserve(target);

                // This flag is used by background images which need to lazy-load.
                target.setAttribute('data-intersected', 'true');

                if (target.tagName === 'IMG') {
                    // apply the image source.
                    if (inPicture) {
                        if (target.parentElement?.tagName === 'PICTURE') {
                            applyPictureSrc(target.parentElement as HTMLPictureElement);
                        }
                        return;
                    }

                    if (src || srcset) {
                        applyImageSrc(target as HTMLImageElement);
                    }
                }
            }

            if (observed === undefined) {
                // This image is being observed for the first time.
                if (!entry.isIntersecting) {
                    // This flag is used to control whether we perform a fade-in animation on the image.
                    target.setAttribute('data-hidden-on-mount', 'true');
                }
                target.setAttribute('data-observed', 'true');
            }
        });
    }, []);

    const handleRef = React.useCallback(
        (element: HTMLElement | null) => {
            if (isClient && element) {
                if (!observerRef.current) {
                    observerRef.current = new IntersectionObserver(callback, options);
                }
                observerRef.current.observe(element);
            }
        },
        [callback, isClient, options]
    );

    useEffect(() => {
        return () => {
            observerRef.current?.disconnect();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const values: LazyImageContextValues = React.useMemo(
        () => ({
            handleRef,
        }),
        [handleRef]
    );

    return <LazyImageContext.Provider value={values}>{children}</LazyImageContext.Provider>;
}
